// Pure, testable logic for a direct room "ask an agent" interaction. The shared
// backend owns routing/cooldown-bypass/verified context; this only builds the
// request, normalizes the varied response, and runs a bounded/cancellable call.
// It NEVER manufactures client-side chatter.

export const ROOM_ASK = {
  timeoutMs: 20000,
  secondAgentWaitMs: 6000, // default wait before checking for an optional 2nd agent
  minDelayMs: 2000,
  maxDelayMs: 15000,
};

const ROLE_LABELS = { host: "Turf Talk Host", analyst: "Turf Talk Analyst", predictor: "Turf Talk Predictor" };

export function agentDisplayName(role, roles) {
  const found = Array.isArray(roles) ? roles.find((r) => r.role === role) : null;
  return (found && found.display_name) || ROLE_LABELS[role] || (role ? role[0].toUpperCase() + role.slice(1) : "Turf Talk AI");
}

// Friendly copy for backend-enforced non-actions (cooldown / quiet / kill).
export function mentionNote(reason = "") {
  const r = String(reason).toLowerCase();
  if (r.includes("cooldown") || r.includes("duplicate") || r.includes("dedup")) return "An agent just spoke — give it a moment.";
  if (r.includes("kill") || r.includes("disabled") || r.includes("off")) return "Agents are paused right now.";
  if (r.includes("rate")) return "Agents are taking a breather — try again shortly.";
  if (r.includes("no_action") || r.includes("state")) return "Nothing to add right now — the room's active.";
  return "The agent didn't chime in this time.";
}

// The explicit selected-agent request payload. Reusing the same idempotency key
// on a retry lets the backend dedupe so a retry never duplicates work.
export function buildMentionPayload(role, { idempotencyKey } = {}) {
  const payload = { role };
  if (idempotencyKey) payload.idempotency_key = idempotencyKey;
  return payload;
}

export function clampAskDelay(seconds, cfg = ROOM_ASK) {
  const n = Number(seconds);
  const ms = Number.isFinite(n) && n > 0 ? n * 1000 : cfg.secondAgentWaitMs;
  return Math.min(cfg.maxDelayMs, Math.max(cfg.minDelayMs, ms));
}

// Normalize the varied mention responses into a stable shape without inventing
// content. Supports one optional delayed second-agent event.
export function normalizeMentionResponse(r) {
  const d = r && typeof r === "object" ? r : {};
  const messages = Array.isArray(d.messages) ? d.messages : (d.message ? [d.message] : []);
  const acted = d.acted === true || messages.length > 0;
  const secondAgent = d.second_agent_event || d.second_agent || d.followup || null;
  const expectSecondAgent = !!secondAgent || d.expect_second_agent === true || d.second_agent_pending === true;
  return {
    acted,
    reason: d.reason || null,
    note: acted ? null : mentionNote(d.reason),
    messages,
    secondAgent,
    expectSecondAgent,
    secondAgentDelayMs: clampAskDelay(d.second_agent_after ?? d.retry_after),
  };
}

// Run a direct ask with a bounded timeout + cancellation. `ask` and `delay` are
// injectable for tests. Resolves { status: ok|timeout|error|cancelled, ... }.
export async function runRoomAsk({ ask, signal, timeoutMs = ROOM_ASK.timeoutMs, delay } = {}) {
  if (signal && signal.aborted) return { status: "cancelled" };
  const wait = delay || ((ms) => new Promise((res) => setTimeout(res, ms)));
  const TIMEOUT = Symbol("timeout");
  let outcome;
  try {
    outcome = await Promise.race([
      Promise.resolve().then(ask).then((r) => ({ r })),
      wait(timeoutMs).then(() => TIMEOUT),
    ]);
  } catch (error) {
    if (signal && signal.aborted) return { status: "cancelled" };
    return { status: "error", error };
  }
  if (signal && signal.aborted) return { status: "cancelled" };
  if (outcome === TIMEOUT) return { status: "timeout" };
  return { status: "ok", response: normalizeMentionResponse(outcome.r) };
}
